import { Box, Paper, Popper, SxProps, Theme } from "@mui/material";
import { ReactNode, useCallback, useEffect, useRef, useState } from "react";

interface HoverPopupProps {
  trigger: ReactNode;
  children: ReactNode;
  placement?:
    | "top"
    | "bottom"
    | "left"
    | "right"
    | "top-start"
    | "top-end"
    | "bottom-start"
    | "bottom-end";
  offset?: [number, number];
  maxWidth?: string | number;
  disabled?: boolean;
  fetchData?: () => void;
  paperSx?: SxProps<Theme>;
}

export const HoverPopper = ({
  trigger,
  children,
  placement = "top-start",
  offset = [0, 8],
  maxWidth = "none",
  disabled = false,
  fetchData,
  paperSx,
}: HoverPopupProps) => {
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const triggerRef = useRef<HTMLDivElement | null>(null);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const leaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleMouseEnter = useCallback(() => {
    if (disabled) return;

    if (leaveTimeoutRef.current) {
      clearTimeout(leaveTimeoutRef.current);
      leaveTimeoutRef.current = null;
    }

    if (!open && triggerRef.current) {
      hoverTimeoutRef.current = setTimeout(() => {
        setAnchorEl(triggerRef.current);
        setOpen(true);
        hoverTimeoutRef.current = null;
      }, 200);
    }
  }, [disabled, open]);

  const handleMouseLeave = useCallback(() => {
    if (disabled) return;

    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }

    leaveTimeoutRef.current = setTimeout(() => {
      setOpen(false);
      setAnchorEl(null);
      leaveTimeoutRef.current = null;
    }, 150);
  }, [disabled]);

  const handlePopupMouseEnter = useCallback(() => {
    if (leaveTimeoutRef.current) {
      clearTimeout(leaveTimeoutRef.current);
      leaveTimeoutRef.current = null;
    }
  }, []);

  const handlePopupMouseLeave = useCallback(() => {
    setOpen(false);
    setAnchorEl(null);
  }, []);

  useEffect(() => {
    if (open && fetchData) {
      const delayDebounceFn = setTimeout(() => {
        fetchData();
      }, 500);

      return () => clearTimeout(delayDebounceFn);
    }
  }, [open, fetchData]);

  return (
    <>
      <Box
        ref={triggerRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        sx={{ display: "inline-block", borderRadius: "8px" }}
      >
        {trigger}
      </Box>
      <Popper
        open={open}
        anchorEl={anchorEl}
        placement={placement}
        modifiers={[
          {
            name: "offset", // Position offset from anchor
            options: {
              offset: offset,
            },
          },
          {
            name: "flip", // Auto-flip when doesn't fit
            options: {
              fallbackPlacements: [
                "top-start",
                "bottom-start",
                "bottom-end",
                "right-start",
                "left-start",
              ],
              boundary: "viewport",
              padding: 8,
            },
          },
          {
            name: "preventOverflow", // Keep within viewport
            options: {
              boundary: "viewport",
              padding: 8,
              mainAxis: true, // Prevent overflow on main axis
              altAxis: true, // Prevent overflow on cross axis
            },
          },
        ]}
        style={{ zIndex: 1500 }}
      >
        <Paper
          elevation={8}
          onMouseEnter={handlePopupMouseEnter}
          onMouseLeave={handlePopupMouseLeave}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          sx={[
            {
              maxWidth,
              maxHeight: "80vh",
              backgroundColor: "background.paper",
              border: "1px solid",
              borderColor: "divider",
              boxSizing: "border-box",
              display: "flex",
              flexDirection: "column",
              p: 0,
            },
            ...(Array.isArray(paperSx) ? paperSx : paperSx ? [paperSx] : []),
          ]}
        >
          {children}
          {/*  Consider adding actions banner here and passing actions as props */}
        </Paper>
      </Popper>
    </>
  );
};
